import { Fragment, useState, useEffect, useRef } from "react";
import {
  sendEmails, getTemplates, getGroups, getSendLogs, getSendLog, resendLog, cancelLog, getSmtpConfigs,
  getAttachments, uploadAttachment, getLetterheads, uploadLetterhead,
} from "../../mailerApi";
import RichTextEditor from "../../components/RichTextEditor";
import "./Mailer.css";

const GEO_MONTHS_LONG = [
  "იანვარი", "თებერვალი", "მარტი", "აპრილი", "მაისი", "ივნისი",
  "ივლისი", "აგვისტო", "სექტემბერი", "ოქტომბერი", "ნოემბერი", "დეკემბერი",
];
const GEO_WEEKDAYS_SHORT = ["ორშ", "სამ", "ოთხ", "ხუთ", "პარ", "შაბ", "კვ"];

function CalendarGrid({ selectedDates, onToggleDate }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  // Build a 6×7 grid starting from the Monday of the week that contains day 1
  const firstDayWeek = viewMonth.getDay() === 0 ? 7 : viewMonth.getDay(); // Sun=7, Mon=1...
  const offset = firstDayWeek - 1;
  const gridStart = new Date(viewMonth);
  gridStart.setDate(1 - offset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  function prevMonth() {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  }
  function nextMonth() {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  }

  // Disable prev when viewing current month (no point going to past months)
  const atCurrentMonth = viewMonth.getFullYear() === today.getFullYear() && viewMonth.getMonth() === today.getMonth();

  function dKey(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  return (
    <div className="cal">
      <div className="cal-nav">
        <button
          type="button"
          className="cal-nav-btn"
          onClick={prevMonth}
          disabled={atCurrentMonth}
          aria-label="წინა თვე"
        >
          ‹
        </button>
        <span className="cal-month">{GEO_MONTHS_LONG[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
        <button type="button" className="cal-nav-btn" onClick={nextMonth} aria-label="შემდეგი თვე">›</button>
      </div>
      <div className="cal-weekdays">
        {GEO_WEEKDAYS_SHORT.map(w => <span key={w}>{w}</span>)}
      </div>
      <div className="cal-days">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === viewMonth.getMonth();
          const isToday = d.getTime() === today.getTime();
          const isPast = d < today;
          const k = dKey(d);
          const isSelected = selectedDates.has(k);
          const cls = ["cal-day"];
          if (!inMonth) cls.push("cal-day--out");
          if (isToday) cls.push("cal-day--today");
          if (isPast) cls.push("cal-day--past");
          if (isSelected) cls.push("cal-day--selected");
          return (
            <button
              key={i}
              type="button"
              className={cls.join(" ")}
              disabled={isPast}
              onClick={() => onToggleDate(k)}
              aria-pressed={isSelected}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GroupTree({ groups, selectedGroups, toggleGroup, expanded, toggleExpand, depth = 0 }) {
  return groups.map(g => {
    const hasChildren = g.children && g.children.length > 0;
    const isOpen = expanded.has(g.id);
    const isChecked = selectedGroups.includes(g.id);
    return (
      <div key={g.id}>
        <label className="checkbox-item" style={{ paddingLeft: depth * 20, display: "flex", alignItems: "center", gap: 4 }}>
          {hasChildren ? (
            <button
              type="button"
              className="btn-icon expand-toggle"
              onClick={(e) => { e.preventDefault(); toggleExpand(g.id); }}
              title={isOpen ? "ჩაკეცვა" : "გაშლა"}
            >
              {isOpen ? "▾" : "▸"}
            </button>
          ) : (
            <span style={{ display: "inline-block", width: 18 }} />
          )}
          <input type="checkbox" checked={isChecked} onChange={() => toggleGroup(g.id)} />
          <span className={depth > 0 ? "subgroup-label" : ""}>{g.name}</span>
          <span className="group-count">({g.total_contact_count})</span>
          {isChecked && hasChildren && (
            <span className="group-hint" style={{ marginLeft: 6, fontSize: 11, color: "#016394" }}>
              ყველა ქვეჯგუფი ჩართულია
            </span>
          )}
        </label>
        {hasChildren && isOpen && (
          <GroupTree
            groups={g.children}
            selectedGroups={selectedGroups}
            toggleGroup={toggleGroup}
            expanded={expanded}
            toggleExpand={toggleExpand}
            depth={depth + 1}
          />
        )}
      </div>
    );
  });
}

export default function SendPage() {
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [selectedSmtp, setSelectedSmtp] = useState("");
  const [form, setForm] = useState({ subject: "", body: "", template_id: null });
  const [sendMode, setSendMode] = useState("all"); // all, groups, manual
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  function toggleExpandGroup(id) {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  const [manualEmails, setManualEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [failureDetails, setFailureDetails] = useState(null); // {log, loading}
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("compose"); // compose, history
  const [attachments, setAttachments] = useState([]); // library [{id,name,file_url,size_bytes}]
  const [selectedAttachments, setSelectedAttachments] = useState([]); // [id]
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [letterheads, setLetterheads] = useState([]);
  const [selectedLetterhead, setSelectedLetterhead] = useState(null); // id|null
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
  const letterheadInputRef = useRef(null);

  // Send mode: choice modal + schedule editor
  const [showSendChoice, setShowSendChoice] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleSlots, setScheduleSlots] = useState([]); // [{datetime: "YYYY-MM-DDTHH:MM", count: number|""}]
  const [expandedLogs, setExpandedLogs] = useState(new Set()); // log ids whose batches are expanded

  useEffect(() => {
    getTemplates().then(d => setTemplates(Array.isArray(d) ? d : d.results || [])).catch(() => {});
    getGroups().then(d => setGroups(Array.isArray(d) ? d : d.results || [])).catch(() => {});
    getSmtpConfigs().then(d => {
      const list = Array.isArray(d) ? d : d.results || [];
      setSmtpConfigs(list.filter(c => c.is_active));
    }).catch(() => {});
    loadAttachments();
    loadLetterheads();
    loadLogs();
  }, []);

  function loadAttachments() {
    getAttachments().then(d => {
      setAttachments(Array.isArray(d) ? d : (d.results || []));
    }).catch(() => {});
  }

  function loadLetterheads() {
    getLetterheads().then(d => {
      setLetterheads(Array.isArray(d) ? d : (d.results || []));
    }).catch(() => {});
  }

  async function handleLetterheadUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLetterhead(true);
    setError("");
    try {
      const item = await uploadLetterhead(file, file.name);
      setLetterheads(prev => [item, ...prev]);
      setSelectedLetterhead(item.id);
    } catch (err) {
      setError(err.message);
    }
    setUploadingLetterhead(false);
    if (letterheadInputRef.current) letterheadInputRef.current.value = "";
  }

  function toggleAttachment(id) {
    setSelectedAttachments(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  }

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    setError("");
    try {
      const att = await uploadAttachment(file, file.name);
      setAttachments(prev => [att, ...prev]);
      setSelectedAttachments(prev => [...prev, att.id]);
    } catch (err) {
      setError(err.message);
    }
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Auto-refresh logs when there are active sends. Throttled sends wake up
  // every 5 min via beat so a 15s poll catches the status flip to "sending".
  // Scheduled campaigns get a 60s tick so per-batch progress updates without
  // needing a manual refresh when an ETA fires.
  useEffect(() => {
    if (tab !== "history") return;
    const hasSending = logs.some(l => l.status === "sending");
    const hasActiveBatch = logs.some(l => (l.batches || []).some(b => b.status === "sending"));
    const hasThrottled = logs.some(l => l.status === "throttled");
    const hasScheduled = logs.some(l => l.status === "scheduled");
    if (!hasSending && !hasActiveBatch && !hasThrottled && !hasScheduled) return;
    const ms = (hasSending || hasActiveBatch) ? 3000 : (hasThrottled ? 15000 : 60000);
    const interval = setInterval(loadLogs, ms);
    return () => clearInterval(interval);
  }, [logs, tab]);

  function loadLogs() {
    getSendLogs().then(d => {
      const list = Array.isArray(d) ? d : (d.results || []);
      setLogs(list);
    }).catch(() => {});
  }

  // Total recipient count for the current selection (for confirmation prompt)
  function estimateRecipientCount() {
    if (sendMode === "all") {
      // sum of all top-level groups' total_contact_count
      return groups.reduce((acc, g) => acc + (g.total_contact_count || 0), 0);
    }
    if (sendMode === "groups") {
      // Walk tree, sum total_contact_count for selected nodes (descendants already included)
      let total = 0;
      const visit = (gs) => {
        for (const g of gs) {
          if (selectedGroups.includes(g.id)) {
            total += g.total_contact_count || 0;
            // skip descendants — backend includes them automatically
            continue;
          }
          if (g.children && g.children.length) visit(g.children);
        }
      };
      visit(groups);
      return total;
    }
    if (sendMode === "manual") {
      return manualEmails.split(/[,;\n]/).filter(e => e.trim()).length;
    }
    return 0;
  }

  function loadTemplate(id) {
    const t = templates.find(t => t.id === Number(id));
    if (t) setForm({ subject: t.subject, body: t.body, template_id: t.id });
  }

  function toggleGroup(id) {
    setSelectedGroups(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  }

  function insertPlaceholder(placeholder) {
    setForm(prev => ({ ...prev, body: prev.body + placeholder }));
  }

  function dateKey(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  function parseDateKey(str) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  function formatGeoDate(dateStr) {
    const months = ["იან", "თებ", "მარ", "აპრ", "მაი", "ივნ", "ივლ", "აგვ", "სექ", "ოქტ", "ნოე", "დეკ"];
    const d = parseDateKey(dateStr);
    return `${d.getDate()} ${months[d.getMonth()]}`;
  }

  async function handleSend() {
    if (smtpConfigs.length > 0 && !selectedSmtp) {
      setError("აირჩიეთ გამგზავნი ანგარიში");
      return;
    }
    if (!form.subject || !form.body) {
      setError("სათაური და ტექსტი სავალდებულოა");
      return;
    }

    const payload = {
      subject: form.subject,
      body: form.body,
      template_id: form.template_id,
      send_to_all: sendMode === "all",
      group_ids: sendMode === "groups" ? selectedGroups : [],
      contact_ids: [],
      manual_emails: [],
      smtp_config_id: selectedSmtp ? Number(selectedSmtp) : null,
      attachment_ids: selectedAttachments,
      letterhead_id: selectedLetterhead || null,
    };

    if (sendMode === "manual") {
      const emails = manualEmails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
      const invalid = emails.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      if (invalid.length) {
        setError(`არასწორი მეილ(ებ)ი: ${invalid.join(", ")}`);
        return;
      }
      if (!emails.length) {
        setError("მეილები არ არის შეყვანილი");
        return;
      }
      payload.manual_emails = emails;
      payload.send_to_all = false;
    }

    setError("");
    setResult(null);
    setPendingPayload(payload);
    setPendingTotal(sendMode === "manual" ? payload.manual_emails.length : estimateRecipientCount());
    setShowSendChoice(true);
  }

  async function doSend(extra = {}) {
    if (!pendingPayload) return;
    setShowSendChoice(false);
    setShowSchedule(false);
    setSending(true);
    try {
      const data = await sendEmails({ ...pendingPayload, ...extra });
      setResult(data);
      setTab("history");
      loadLogs();
      setPendingPayload(null);
    } catch (e) {
      setError(e.message);
    }
    setSending(false);
  }

  function chooseScheduled() {
    setShowSendChoice(false);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduleSlots([{ date: dateKey(tomorrow), time: "09:00", count: pendingTotal }]);
    setShowSchedule(true);
  }

  function toggleCalendarDate(dateStr) {
    const exists = scheduleSlots.find(s => s.date === dateStr);
    if (exists) {
      // Don't let users empty the schedule entirely from the calendar — leaves
      // them stuck with no rows. The footer's Cancel handles "throw it all out".
      if (scheduleSlots.length === 1) return;
      setScheduleSlots(scheduleSlots.filter(s => s.date !== dateStr));
      return;
    }
    const totalAssigned = scheduleSlots.reduce((acc, s) => acc + Number(s.count || 0), 0);
    const remaining = Math.max(0, pendingTotal - totalAssigned);
    const newSlot = { date: dateStr, time: "09:00", count: remaining };
    const next = [...scheduleSlots, newSlot].sort((a, b) => a.date.localeCompare(b.date));
    setScheduleSlots(next);
  }

  function updateScheduleSlot(idx, field, value) {
    setScheduleSlots(scheduleSlots.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  function removeScheduleSlot(idx) {
    if (scheduleSlots.length === 1) return;
    setScheduleSlots(scheduleSlots.filter((_, i) => i !== idx));
  }

  function confirmSchedule() {
    const totalScheduled = scheduleSlots.reduce((acc, s) => acc + Number(s.count || 0), 0);
    if (totalScheduled !== pendingTotal) {
      setError(`დაგეგმილი (${totalScheduled.toLocaleString("ka-GE")}) უნდა ემთხვეოდეს მთლიან რაოდენობას (${pendingTotal.toLocaleString("ka-GE")})`);
      return;
    }
    const now = new Date();
    for (const s of scheduleSlots) {
      if (!s.date || !s.time) {
        setError("ყველა დღეზე უნდა იყოს მითითებული თარიღი და დრო");
        return;
      }
      const [h, mn] = s.time.split(":").map(Number);
      const d = parseDateKey(s.date);
      d.setHours(h, mn, 0, 0);
      if (d <= now) {
        setError("ყველა თარიღი მომავალში უნდა იყოს");
        return;
      }
      if (Number(s.count) <= 0) {
        setError("ყველა დღეზე რაოდენობა > 0 უნდა იყოს");
        return;
      }
    }
    const schedule = scheduleSlots.map(s => {
      const [h, mn] = s.time.split(":").map(Number);
      const d = parseDateKey(s.date);
      d.setHours(h, mn, 0, 0);
      return { datetime: d.toISOString(), count: Number(s.count) };
    });
    setError("");
    doSend({ schedule });
  }

  function toggleExpandLog(id) {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleResend(logId, failedOnly) {
    setResending(logId);
    setError("");
    try {
      await resendLog(logId, failedOnly);
      loadLogs();
    } catch (e) {
      setError(e.message);
    }
    setResending(null);
  }

  async function handleShowFailures(logId) {
    setFailureDetails({ log: null, loading: true });
    setError("");
    try {
      const log = await getSendLog(logId);
      setFailureDetails({ log, loading: false });
    } catch (e) {
      setError(e.message);
      setFailureDetails(null);
    }
  }

  async function handleCancel(logId) {
    if (!window.confirm("გსურს ამ გაგზავნის გაუქმება? უკვე გაგზავნილი წერილები დარჩება, გაუგზავნელები არ გაიგზავნება.")) return;
    setCancelling(logId);
    setError("");
    try {
      await cancelLog(logId);
      loadLogs();
    } catch (e) {
      setError(e.message);
    }
    setCancelling(null);
  }

  return (
    <div className="mailer-page">
      <div className="mailer-header">
        <h1>გაგზავნა</h1>
        <div className="tab-buttons">
          <button className={`tab-btn ${tab === "compose" ? "active" : ""}`} onClick={() => setTab("compose")}>კომპოზიცია</button>
          <button className={`tab-btn ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}>ისტორია</button>
        </div>
      </div>

      {error && <div className="mailer-error">{error}<button onClick={() => setError("")}>×</button></div>}

      {tab === "compose" ? (
        <div className="send-layout">
          <div className="send-compose">
            <div className="form-group">
              <label>შაბლონიდან ჩატვირთვა</label>
              <select onChange={(e) => e.target.value && loadTemplate(e.target.value)} defaultValue="">
                <option value="">აირჩიეთ შაბლონი...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.subject}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>სათაური</label>
              <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="წერილის სათაური (მაგ: გამარჯობა {name})" />
            </div>
            <div className="form-group">
              <label>ტექსტი</label>
              <RichTextEditor
                value={form.body}
                onChange={(html) => setForm(prev => ({ ...prev, body: html }))}
                placeholder="წერილის ტექსტი..."
                minRows={12}
              />
            </div>
            <div className="personalization-bar">
              <span className="personalization-label">პერსონალიზაცია:</span>
              <button type="button" className="placeholder-btn" onClick={() => insertPlaceholder("{name}")}>
                {"{name}"} — სახელი
              </button>
              <button type="button" className="placeholder-btn" onClick={() => insertPlaceholder("{email}")}>
                {"{email}"} — მეილი
              </button>
            </div>

            <div className="attachments-section">
              <div className="attachments-header">
                <label className="attachments-title">ტიტულარი (მეილის ბოლოში)</label>
                <div className="attachments-actions">
                  <input
                    ref={letterheadInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    style={{ display: "none" }}
                    onChange={handleLetterheadUpload}
                  />
                  <button
                    type="button"
                    className="placeholder-btn"
                    onClick={() => letterheadInputRef.current?.click()}
                    disabled={uploadingLetterhead}
                  >
                    {uploadingLetterhead ? "იტვირთება..." : "+ ახლის ატვირთვა"}
                  </button>
                </div>
              </div>
              {letterheads.length === 0 ? (
                <div className="attachments-empty">
                  ტიტულარი არ გაქვს ატვირთული — ატვირთე სურათი და მონიშნე.
                </div>
              ) : (
                <div className="letterhead-picker">
                  <label className={`letterhead-option${!selectedLetterhead ? " selected" : ""}`}>
                    <input
                      type="radio"
                      name="letterhead"
                      checked={!selectedLetterhead}
                      onChange={() => setSelectedLetterhead(null)}
                    />
                    <span className="letterhead-none">გარეშე</span>
                  </label>
                  {letterheads.map(l => (
                    <label key={l.id} className={`letterhead-option${selectedLetterhead === l.id ? " selected" : ""}`}>
                      <input
                        type="radio"
                        name="letterhead"
                        checked={selectedLetterhead === l.id}
                        onChange={() => setSelectedLetterhead(l.id)}
                      />
                      {l.image_url && <img src={l.image_url} alt="" className="letterhead-thumb-sm" />}
                      <span className="letterhead-name" title={l.name}>{l.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="attachments-section">
              <div className="attachments-header">
                <label className="attachments-title">დანართები (PDF და სხვ.)</label>
                <div className="attachments-actions">
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                  <button
                    type="button"
                    className="placeholder-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                  >
                    {uploadingFile ? "იტვირთება..." : "+ ახლის ატვირთვა"}
                  </button>
                </div>
              </div>
              {attachments.length === 0 ? (
                <div className="attachments-empty">
                  ბიბლიოთეკა ცარიელია — ატვირთე ფაილი და მიამაგრე.
                </div>
              ) : (
                <div className="attachments-list">
                  {attachments.map(a => {
                    const checked = selectedAttachments.includes(a.id);
                    return (
                      <label key={a.id} className={`attachment-item${checked ? " selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttachment(a.id)}
                        />
                        <span className="attachment-icon" aria-hidden="true">📎</span>
                        <span className="attachment-name" title={a.name}>{a.name}</span>
                        <span className="attachment-size">{formatSize(a.size_bytes)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {selectedAttachments.length > 0 && (
                <div className="attachments-selected-hint">
                  არჩეულია {selectedAttachments.length} ფაილი — დაერთვება ყველა მიმღებს
                </div>
              )}
            </div>
          </div>

          <div className="send-options">
            {smtpConfigs.length > 0 && (
              <div className="form-group sender-select">
                <label>გამგზავნი ანგარიში</label>
                <select value={selectedSmtp} onChange={e => setSelectedSmtp(e.target.value)}>
                  <option value="">აირჩიეთ გამგზავნი...</option>
                  {smtpConfigs.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.display_name ? `${c.display_name} (${c.username})` : c.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <h3>მიმღებები</h3>
            <div className="send-mode-options">
              <label className={`mode-option ${sendMode === "all" ? "active" : ""}`}>
                <input type="radio" name="mode" checked={sendMode === "all"} onChange={() => setSendMode("all")} />
                ყველა აქტიური კონტაქტი
              </label>
              <label className={`mode-option ${sendMode === "groups" ? "active" : ""}`}>
                <input type="radio" name="mode" checked={sendMode === "groups"} onChange={() => setSendMode("groups")} />
                ჯგუფების მიხედვით
              </label>
              <label className={`mode-option ${sendMode === "manual" ? "active" : ""}`}>
                <input type="radio" name="mode" checked={sendMode === "manual"} onChange={() => setSendMode("manual")} />
                კონკრეტული მეილები
              </label>
            </div>

            {sendMode === "groups" && (
              <div className="group-checklist">
                <GroupTree
                  groups={groups}
                  selectedGroups={selectedGroups}
                  toggleGroup={toggleGroup}
                  expanded={expandedGroups}
                  toggleExpand={toggleExpandGroup}
                />
              </div>
            )}

            {sendMode === "manual" && (
              <div className="form-group">
                <textarea
                  value={manualEmails}
                  onChange={e => setManualEmails(e.target.value)}
                  rows={5}
                  placeholder={"მეილები (მძიმით ან ახალ ხაზზე)\nuser1@example.com\nuser2@example.com"}
                />
              </div>
            )}

            <button className="btn btn-primary btn-send" onClick={handleSend} disabled={sending}>
              {sending ? "იგზავნება..." : "გაგზავნა"}
            </button>

            {result && (
              <div className="send-result">
                <div className="stat success">
                  <span className="stat-number">{result.success_count}</span>
                  <span className="stat-label">გაგზავნილი</span>
                </div>
                <div className="stat danger">
                  <span className="stat-number">{result.fail_count}</span>
                  <span className="stat-label">წარუმატებელი</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mailer-table-wrap">
          <table className="mailer-table">
            <thead>
              <tr>
                <th>სათაური</th>
                <th>გამგზავნი</th>
                <th>მიმღებები</th>
                <th>გაგზავნილი</th>
                <th>წარუმატებელი</th>
                <th>სტატუსი</th>
                <th>თარიღი</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan="8" className="no-data">ისტორია ცარიელია</td></tr>
              ) : logs.map(l => {
                const hasBatches = (l.batches || []).length > 0;
                const isExpanded = expandedLogs.has(l.id);
                const nextBatch = hasBatches
                  ? l.batches.find(b => b.status === "pending") || l.batches.find(b => b.status === "sending")
                  : null;
                return (
                <Fragment key={l.id}>
                <tr>
                  <td>
                    {hasBatches && (
                      <button
                        type="button"
                        className="batch-expand-toggle"
                        onClick={() => toggleExpandLog(l.id)}
                        title={isExpanded ? "ჩაკეცვა" : "გაშლა"}
                      >
                        {isExpanded ? "▾" : "▸"}
                      </button>
                    )}
                    {l.subject}
                  </td>
                  <td className="sender-cell">{l.sender_display || "—"}</td>
                  <td>{l.recipient_count}</td>
                  <td className="text-success">{l.success_count}</td>
                  <td className="text-danger">
                    {l.fail_count > 0 ? (
                      <button
                        type="button"
                        className="fail-count-btn"
                        onClick={() => handleShowFailures(l.id)}
                        title="წარუმატებლების დეტალები"
                      >
                        {l.fail_count}
                      </button>
                    ) : (
                      l.fail_count
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${l.status}`}>
                      {l.status === "completed"
                        ? "დასრულდა"
                        : l.status === "sending"
                          ? `იგზავნება ${l.success_count + l.fail_count}/${l.recipient_count}`
                          : l.status === "scheduled"
                            ? `დაგეგმილია · ${l.batches?.length || 0} დღე`
                            : l.status === "throttled"
                              ? `⏸ შეჩერდა ${l.success_count}/${l.recipient_count}`
                              : l.status === "cancelled"
                                ? `⛔ გაუქმდა ${l.success_count}/${l.recipient_count}`
                                : l.status === "failed"
                                  ? "შეცდომა"
                                  : l.status}
                    </span>
                    {l.status === "throttled" && l.resume_at && (
                      <div className="resume-info" title={l.throttle_reason || ""}>
                        გაგრძელდება: {new Date(l.resume_at).toLocaleString("ka-GE")}
                      </div>
                    )}
                    {l.status === "scheduled" && nextBatch && (
                      <div className="resume-info">
                        შემდეგი: {new Date(nextBatch.scheduled_at).toLocaleString("ka-GE")}
                      </div>
                    )}
                  </td>
                  <td>{new Date(l.sent_at).toLocaleString("ka-GE")}</td>
                  <td className="actions-cell resend-cell">
                    {(l.status === "sending" || l.status === "throttled" || l.status === "scheduled") && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleCancel(l.id)}
                        disabled={cancelling === l.id}
                        title="გაგზავნის გაუქმება"
                      >
                        {cancelling === l.id ? "..." : "გაუქმება"}
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleResend(l.id, false)}
                      disabled={resending === l.id}
                      title="ყველასთვის ხელახლა გაგზავნა"
                    >
                      {resending === l.id ? "..." : "ხელახლა"}
                    </button>
                    {l.fail_count > 0 && (
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleResend(l.id, true)}
                        disabled={resending === l.id}
                        title="მხოლოდ წარუმატებლებისთვის"
                      >
                        {resending === l.id ? "..." : `${l.fail_count} წარუმატ.`}
                      </button>
                    )}
                  </td>
                </tr>
                {hasBatches && isExpanded && (
                  <tr className="batch-list-row">
                    <td colSpan="8">
                      <div className="batch-list">
                        {l.batches.map((b, i) => (
                          <div key={b.id} className={`batch-item batch-item--${b.status}`}>
                            <span className="batch-day">დღე {i + 1}</span>
                            <span className="batch-when">
                              {new Date(b.scheduled_at).toLocaleString("ka-GE")}
                            </span>
                            <span className="batch-count">
                              {b.success_count}/{b.count}
                              {b.fail_count > 0 && (
                                <span className="batch-fail"> · {b.fail_count} წარუმატ.</span>
                              )}
                            </span>
                            <span className={`batch-status batch-status--${b.status}`}>
                              {b.status === "pending" ? "დაგეგმილია"
                                : b.status === "sending" ? "იგზავნება"
                                : b.status === "completed" ? "დასრულდა"
                                : b.status === "cancelled" ? "გაუქმდა"
                                : "ვერ გაიგზავნა"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {failureDetails && (
        <div className="failures-modal-overlay" onClick={() => setFailureDetails(null)}>
          <div className="failures-modal" onClick={(e) => e.stopPropagation()}>
            <div className="failures-modal-header">
              <h3>წარუმატებლების დეტალები</h3>
              <button type="button" className="failures-modal-close" onClick={() => setFailureDetails(null)}>×</button>
            </div>
            {failureDetails.loading ? (
              <div className="failures-modal-body">იტვირთება...</div>
            ) : (
              <div className="failures-modal-body">
                {(() => {
                  const log = failureDetails.log;
                  const details = log?.failure_details || [];
                  const fallback = log?.failed_emails || [];
                  // Older logs (pre-feature) only have failed_emails — show those without reason
                  const rows = details.length
                    ? details
                    : fallback.map((email) => ({ email, error: "შეცდომის ტექსტი მიუწვდომელია (ძველი ჩანაწერი)" }));
                  if (!rows.length) return <div>წარუმატებელი მიმღები არ არის.</div>;
                  return (
                    <table className="failures-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>მიმღები</th>
                          <th>შეცდომა</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td className="failures-email">{r.email}</td>
                            <td className="failures-error">{r.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {showSendChoice && (
        <div className="send-choice-overlay" onClick={() => setShowSendChoice(false)}>
          <div className="send-choice" onClick={(e) => e.stopPropagation()}>
            <div className="send-choice-header">
              <h3>გაგზავნის ტიპი</h3>
              <button type="button" className="send-choice-close" onClick={() => setShowSendChoice(false)}>×</button>
            </div>
            <div className="send-choice-meta">
              <span><strong>{pendingTotal.toLocaleString("ka-GE")}</strong> მიმღები</span>
              {selectedSmtp && (
                <span className="send-choice-meta-sender">
                  გამგზავნი: <span className="send-choice-meta-mono">{smtpConfigs.find(s => s.id === Number(selectedSmtp))?.username || ""}</span>
                </span>
              )}
            </div>
            <div className="send-choice-options">
              <button type="button" className="send-choice-card" onClick={() => doSend()} disabled={sending}>
                <div className="send-choice-glyph send-choice-glyph--now">→</div>
                <div className="send-choice-title">ახლავე გაგზავნა</div>
                <div className="send-choice-desc">
                  ერთიანად, მთლიანი სია, ფონურ რეჟიმში.
                </div>
              </button>
              <button type="button" className="send-choice-card" onClick={chooseScheduled} disabled={sending}>
                <div className="send-choice-glyph send-choice-glyph--sched">⊟</div>
                <div className="send-choice-title">დღეებზე განაწილება</div>
                <div className="send-choice-desc">
                  სიის თანმიმდევრობით, რამდენიმე დღეზე. Gmail-ის 500/დღე ლიმიტისთვის.
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (() => {
        const totalScheduled = scheduleSlots.reduce((acc, s) => acc + Number(s.count || 0), 0);
        const remaining = pendingTotal - totalScheduled;
        const overLimit = scheduleSlots.some(s => Number(s.count) > 450);
        const now = new Date();
        const allValid = scheduleSlots.length > 0
          && scheduleSlots.every(s => {
            if (!s.date || !s.time || Number(s.count) <= 0) return false;
            const [h, mn] = s.time.split(":").map(Number);
            const d = parseDateKey(s.date);
            d.setHours(h, mn, 0, 0);
            return d > now;
          });
        const canConfirm = allValid && remaining === 0 && !sending;
        const remainingClass = remaining === 0 ? "ok" : remaining > 0 ? "pending" : "over";
        const selectedDateSet = new Set(scheduleSlots.map(s => s.date));
        return (
          <div className="schedule-overlay" onClick={() => setShowSchedule(false)}>
            <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
              <div className="schedule-header">
                <h3>გაგზავნის განრიგი</h3>
                <button type="button" className="schedule-close" onClick={() => setShowSchedule(false)}>×</button>
              </div>

              <div className="schedule-summary">
                <div className="schedule-stat">
                  <span className="schedule-stat-label">მთლიანი</span>
                  <span className="schedule-stat-num">{pendingTotal.toLocaleString("ka-GE")}</span>
                </div>
                <div className="schedule-stat">
                  <span className="schedule-stat-label">დაგეგმილი</span>
                  <span className="schedule-stat-num">{totalScheduled.toLocaleString("ka-GE")}</span>
                </div>
                <div className={`schedule-stat schedule-stat--remaining ${remainingClass}`}>
                  <span className="schedule-stat-label">დარჩენილი</span>
                  <span className="schedule-stat-num">{remaining.toLocaleString("ka-GE")}</span>
                </div>
              </div>

              <div className="schedule-body">
                <CalendarGrid selectedDates={selectedDateSet} onToggleDate={toggleCalendarDate} />

                <div className="schedule-rows">
                  {scheduleSlots.length === 0 ? (
                    <div className="schedule-empty">აირჩიეთ დღე(ები) კალენდარზე ↑</div>
                  ) : (
                    <>
                      <div className="schedule-row schedule-row-head">
                        <span className="schedule-col-num">#</span>
                        <span>დღე</span>
                        <span>დრო</span>
                        <span>რაოდენობა</span>
                        <span></span>
                      </div>
                      {scheduleSlots.map((s, i) => (
                        <div key={s.date} className="schedule-row">
                          <span className="schedule-col-num">{i + 1}</span>
                          <span className="schedule-col-day">{formatGeoDate(s.date)}</span>
                          <input
                            type="time"
                            value={s.time}
                            onChange={(e) => updateScheduleSlot(i, "time", e.target.value)}
                          />
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={s.count}
                            onChange={(e) => updateScheduleSlot(i, "count", e.target.value === "" ? "" : Number(e.target.value))}
                          />
                          <button
                            type="button"
                            className="schedule-row-remove"
                            onClick={() => removeScheduleSlot(i)}
                            disabled={scheduleSlots.length === 1}
                            title="დღის წაშლა"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {overLimit && (
                <div className="schedule-warning">
                  Gmail-ის ლიმიტი 500 წერილია დღეში ერთი ანგარიშიდან. რეკომენდირებულია დღიური რაოდენობა იყოს 450-მდე.
                </div>
              )}

              {remaining !== 0 && (
                <div className="schedule-hint">
                  {remaining > 0
                    ? `დასარიგებელია კიდევ ${remaining.toLocaleString("ka-GE")} მიმღები`
                    : `${(-remaining).toLocaleString("ka-GE")}-ით მეტია, ვიდრე მიმღები გაქვთ`}
                </div>
              )}

              <div className="schedule-footer">
                <button type="button" className="schedule-cancel" onClick={() => setShowSchedule(false)}>
                  გაუქმება
                </button>
                <button
                  type="button"
                  className="schedule-confirm"
                  disabled={!canConfirm}
                  onClick={confirmSchedule}
                >
                  {sending ? "..." : "დაგეგმვა"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
